// A picture of the board for a player's report (owner 2026-10-25). No browser API turns a part of the page into a
// picture without asking: getDisplayMedia (and Chrome's Region / Element Capture built on it) shows the browser's
// permission dialog every time and does not exist on phones. So the picture is drawn here from what the board
// shows: the background, the empty slots and every card's own picture at its place on the screen, in the order
// they lie. The windows, the bars and the ads are left out (a window may show the player's e-mail). The pictures
// come from our own host (SS = FULLADDR), so the canvas may be read. A card turned in 3D is drawn flat.

/** The widest picture sent (px); a phone's small screen is drawn at up to twice its size. */
const MAX_W = 900;

const urlOf = (css: string): string | null => /url\(["']?([^"')]+)["']?\)/.exec(css)?.[1] ?? null;

function load(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const zOf = (el: Element): number => Number(getComputedStyle(el).zIndex) || 0;

/** A JPEG data URL of the board as the player sees it, or null when it cannot be drawn. */
export async function boardShot(): Promise<string | null> {
  try {
    const w = innerWidth;
    const h = innerHeight;
    const scale = Math.min(2, MAX_W / w);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const c = canvas.getContext('2d');
    if (!c) return null;
    c.scale(scale, scale);

    // the background: the body's colour, then its picture as app.css lays it ("cover", top centre)
    const body = getComputedStyle(document.body);
    c.fillStyle = body.backgroundColor || '#2a1a10';
    c.fillRect(0, 0, w, h);
    const bgSrc = urlOf(body.backgroundImage);
    const bg = bgSrc ? await load(bgSrc) : null;
    if (bg) {
      const s = Math.max(w / bg.naturalWidth, h / bg.naturalHeight);
      c.drawImage(bg, (w - bg.naturalWidth * s) / 2, 0, bg.naturalWidth * s, bg.naturalHeight * s);
    }

    // the empty slots (their picture is a CSS background)
    for (const el of document.querySelectorAll<HTMLElement>('.board .blank')) {
      const st = getComputedStyle(el);
      const src = st.display === 'none' || st.visibility === 'hidden' ? null : urlOf(st.backgroundImage);
      const img = src ? await load(src) : null;
      const r = el.getBoundingClientRect();
      if (img && r.width > 0) c.drawImage(img, r.left, r.top, r.width, r.height);
    }

    // the cards, the lowest first; a face-up card shows its face picture, the others their back
    const cards = [...document.querySelectorAll<HTMLElement>('.board .card')]
      .filter((el) => !el.hidden && getComputedStyle(el).visibility !== 'hidden')
      .map((el) => ({ el, z: zOf(el) }))
      .sort((a, b) => a.z - b.z);
    for (const { el } of cards) {
      const img = el.querySelector<HTMLImageElement>(el.dataset.card ? '.card__face' : '.card__back') ?? el.querySelector<HTMLImageElement>('img');
      if (!img || !img.complete || !img.naturalWidth) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0) c.drawImage(img, r.left, r.top, r.width, r.height);
    }
    return canvas.toDataURL('image/jpeg', 0.72);
  } catch {
    return null; // a picture that cannot be read (another host) or no canvas: the report goes without it
  }
}
